import React, { useState, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EllipsisVerticalIcon, ChevronDownIcon, ChevronUpIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import AssignedUsersBubbles from '../AssignedUsersBubbles';

/**
 * PipelineCard - Displays a fianza or fianzaRequerida in the pipeline kanban
 * Handles two types:
 * - type: 'fianzaRequerida' - A requirement not yet fulfilled
 * - type: 'fianza' - An actual emitted fianza
 */
const PipelineCard = ({ item, isDragging = false, onClick, onDiferir, onReactivar, onEditEnProceso }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: item.id,
  });

  const style = {
    transform: isSortableDragging ? undefined : CSS.Transform.toString(transform),
    transition: isSortableDragging ? undefined : transition,
  };

  const isRequerida = item.type === 'fianzaRequerida';
  const isDiferida = item.estatus === 'diferida';
  const isEnProceso = item.estatus === 'en_proceso';
  const hasEnProcesoData = item.afianzadoras && Array.isArray(item.afianzadoras) && item.afianzadoras.length > 0;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleDiferir = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onDiferir?.(item);
  };

  const handleReactivar = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onReactivar?.(item);
  };

  const handleEditEnProceso = (e) => {
    e.stopPropagation();
    onEditEnProceso?.(item);
  };

  // Format currency
  const formatMonto = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Get display values based on type
  const getTipo = () => {
    if (isRequerida) {
      return item.tipo || 'Sin tipo';
    }
    return item.tipoFianza || item.tipo || 'Sin tipo';
  };

  const getMonto = () => {
    if (isRequerida) {
      return item.monto;
    }
    return item.montoAfianzado || item.monto;
  };

  const getPorcentaje = () => {
    if (isRequerida) {
      return item.porcentaje;
    }
    return item.linkedRequerida?.porcentaje;
  };

  const getNumeroFianza = () => {
    if (isRequerida) return null;
    return item.numeroFianza || item.xmlFianza?.noFianza;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`rounded-lg shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${
        item.isConvenio
          ? 'bg-[#53B5CC]/5 border-2 border-[#53B5CC]/40'
          : isRequerida ? 'bg-white border border-dashed border-gray-300' : 'bg-white border border-solid border-zinc-200'
      } ${isSortableDragging ? 'opacity-0' : ''} ${isDragging ? 'shadow-lg ring-2 ring-azul-surelia/30' : ''}`}
    >
      {/* Convenio mark */}
      {item.isConvenio && (
        <div className="bg-[#53B5CC] px-3 py-1 rounded-t-[5px] flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />
          <span
            className="text-[10px] font-bold text-white uppercase tracking-wider truncate"
            title={item.convenioNombre || 'Convenio'}
          >
            {item.convenioNombre || 'Convenio'}
          </span>
        </div>
      )}
      {/* Header - Tipo and badge */}
      <div className="p-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Numero de fianza (only for emitted) */}
            {getNumeroFianza() && (
              <p className="text-xs font-semibold text-azul-surelia mb-0.5">
                #{getNumeroFianza()}
              </p>
            )}
            {/* Tipo */}
            <p className="text-sm font-medium text-gray-800 truncate" title={getTipo()}>
              {getTipo()}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Badge */}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              isRequerida
                ? isDiferida
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-orange-100 text-orange-700'
                : item.pagada
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-100 text-blue-700'
            }`}>
              {isRequerida ? (isDiferida ? 'Diferida' : 'Requerida') : item.pagada ? 'Pagada' : 'Emitida'}
            </span>
            {/* Menu button for fianzasRequeridas */}
            {isRequerida && (
              <div className="relative" ref={menuRef}>
                <button
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  onClick={handleMenuClick}
                >
                  <EllipsisVerticalIcon className="w-4 h-4 text-gray-500" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[120px]">
                    {!isDiferida ? (
                      <button
                        className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                        onClick={handleDiferir}
                      >
                        Diferir
                      </button>
                    ) : (
                      <button
                        className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                        onClick={handleReactivar}
                      >
                        Reactivar
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-3 pb-3 space-y-1">
        {item.isConvenio ? (
          <>
            {/* El fiado es quien tiene que emitir, así que va primero y en
                jerarquía alta; el beneficiario debajo. El convenio ya está en
                la cinta de arriba. */}
            <p className="text-xs text-gray-600 font-medium truncate" title={item.fiadoNombre || ''}>
              {item.fiadoNombre || 'Sin fiado'}
            </p>
            <p className="text-xs text-gray-500 truncate" title={item.beneficiarioNombre || ''}>
              <span className="text-gray-400">Benef. </span>
              {item.beneficiarioNombre || 'Sin beneficiario'}
            </p>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-600 font-medium truncate" title={item.clientName}>
              {item.clientName}
            </p>
            {/* Una requerida todavía no tiene XML, así que no hay beneficiario
                que mostrar: se omite el renglón en vez de dejarlo vacío. */}
            {item.beneficiarioNombre && (
              <p className="text-xs text-gray-500 truncate" title={item.beneficiarioNombre}>
                <span className="text-gray-400">Benef. </span>
                {item.beneficiarioNombre}
              </p>
            )}
          </>
        )}
        <p className="text-xs text-gray-500 truncate" title={item.contractTitle}>
          {item.contractTitle}
        </p>
        {item.assignedUsers?.length > 0 && (
          <AssignedUsersBubbles userIds={item.assignedUsers} size="sm" maxDisplay={3} />
        )}
        <div className="flex items-center justify-between pt-1">
          <span className="text-sm font-semibold text-azul-surelia">{formatMonto(getMonto())}</span>
          {getPorcentaje() && (
            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {getPorcentaje()}%
            </span>
          )}
        </div>
        {isRequerida && (
          <p className="text-[10px] text-gray-400">
            {item.momento === 'durante' ? 'Durante' :
             item.momento === 'despues_termino' ? 'Después del término' :
             'Antes del inicio'}
          </p>
        )}
      </div>

      {/* Expandable En Proceso details */}
      {isEnProceso && hasEnProcesoData && (
        <div className="border-t border-gray-100">
          <button
            className="w-full px-3 py-1.5 flex items-center justify-between text-xs text-gray-500 hover:bg-gray-50 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            <span>Detalles del proceso</span>
            {expanded ? (
              <ChevronUpIcon className="w-3.5 h-3.5" />
            ) : (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            )}
          </button>
          {expanded && (
            <div className="px-3 pb-3 space-y-2 bg-gray-50/50">
              {item.afianzadoras && Array.isArray(item.afianzadoras) && item.afianzadoras.length > 0 && (
                <div className="space-y-2">
                  {item.afianzadoras.map((af, idx) => (
                    <div key={idx} className="border-l-2 border-azul-surelia/30 pl-2">
                      <p className="text-xs font-medium text-gray-800">{af.nombre}</p>
                      {af.comentarios && (
                        <p className="text-[10px] text-gray-500 mt-0.5">{af.comentarios}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Edit button */}
              <button
                className="flex items-center gap-1 text-xs text-azul-surelia hover:text-azul-surelia/80 transition-colors mt-2"
                onClick={handleEditEnProceso}
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />
                Editar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PipelineCard;
